"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, LucideIcon, Calendar, Clock } from "lucide-react";
import { SafeHTMLRenderer } from "@utils/homeDataLoader";
import CountdownTimer from "@components/utils/CountdownTimer";

export interface PreviewCardProps {
  id: string;
  href: string;
  title: string;
  image: {
    src: string;
    alt: string;
    sizes?: string;
  };
  tag?: {
    label: string;
    icon?: LucideIcon;
  };
  description?: string;
  excerpt?: string;
  metrics?: string[];
  ariaLabel?: string;
  tagPosition?: "left" | "right";
  onClick?: () => void;
  date?: string;
  showCountdown?: boolean;
}

export default function PreviewCard({
  id,
  href,
  title,
  image,
  tag,
  description,
  excerpt,
  metrics,
  ariaLabel,
  tagPosition = "left",
  onClick,
  date,
  showCountdown = false,
}: PreviewCardProps) {
  const [isMounted, setIsMounted] = useState(false);
  const isCalendlyLink = href.includes("calendly.com");
  
  useEffect(() => {
    setIsMounted(true);
  }, []);
  
  const tagAlignment = tagPosition === "left" ? "left-4" : "right-4";
  
  // Determine content based on description or excerpt
  const content = description || excerpt;
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };
  
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const cardContent = (
    <>
      <div className="relative h-48 overflow-hidden">
        <Image
          src={image.src}
          alt={image.alt}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-300"
          sizes={image.sizes || "(max-width: 1024px) 100vw, 50vw"}
          loading="lazy"
        />
        {tag && (
          <div
            className={`absolute top-4 ${tagAlignment} bg-abis-accent px-3 py-1 rounded-full text-xs font-semibold text-abis-primary flex items-center gap-1.5`}
          >
            {tag.icon && (
              <tag.icon className="w-3 h-3 flex-shrink-0 text-abis-primary" />
            )}
            <span>{tag.label}</span>
          </div>
        )}
      </div>
      <div className="p-6">
        <h3
          className="text-xl font-bold text-abis-primary mb-3 group-hover:text-abis-accent transition-colors"
          style={{
            fontFamily: "var(--font-kanit, Kanit, sans-serif)",
          }}
        >
          {title}
        </h3>
        
        {/* Date Display */}
        {date && (
          <div className="mb-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4 text-abis-primary" />
              <span className="font-medium">{formatDate(date)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4 text-abis-primary" />
              <span>{formatTime(date)} Uhr</span>
            </div>
          </div>
        )}
        
        {/* Countdown Timer */}
        {date && showCountdown && isMounted && (
          <div className="mb-4 p-3 bg-gradient-to-r from-abis-primary/5 to-abis-accent/5 rounded-lg border border-abis-accent/20">
            <div className="text-xs font-semibold text-abis-primary mb-2 text-center">
              Webinar startet in:
            </div>
            <CountdownTimer 
              targetDate={date} 
              variant="compact"
              className="justify-center"
              labels={{
                days: "Tage",
                hours: "Std",
                minutes: "Min",
                seconds: "Sek"
              }}
            />
          </div>
        )}
        
        {content && (
          <p className="text-gray-700 mb-4 leading-relaxed">
            <SafeHTMLRenderer content={content} />
          </p>
        )}
        {metrics && metrics.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {metrics.map((metric, index) => (
              <span
                key={`${id}-metric-${index}`}
                className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
              >
                {metric}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center text-abis-primary font-semibold group-hover:text-abis-accent transition-colors">
          Mehr erfahren
          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </>
  );

  if (onClick) {
    return (
      <div
        onClick={onClick}
        className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={ariaLabel || `Mehr erfahren über ${title}`}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <Link
      href={href}
      target={isCalendlyLink ? "_blank" : undefined}
      rel={isCalendlyLink ? "nofollow noopener noreferrer" : undefined}
      className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
      aria-label={ariaLabel || `Mehr erfahren über ${title}`}
    >
      {cardContent}
    </Link>
  );
}
